/**
 * תפעול LabTop — "מאיפה מגיע הכסף בפועל ומה מצב הקריאות והמנויים" (spec-business §4.6).
 * Read-only (spec §9): the whole screen carries the "יתחבר ל-LabTop" tag while the adapter is
 * in demo mode, and nothing here is editable.
 */

import { useState } from 'react';
import type { CallStatus, OpsCallsResponse, OpsSubscriptionsResponse, OpsSummary, ServiceLine, SummaryResponse } from '../../../api-types.ts';
import { fmtDate, fmtMoney, fmtRatio, fmtShortDate, fmtTime, monthName, monthShort } from '../format.ts';
import { CALL_STATUS, CALL_TYPE, SERVICE_LINE, SUBSCRIPTION_STATUS, he } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { Card, CardHead, Chip, DashedTag, EmptyState, FilterChip, Ltr, Money, Num, Skeleton, Tile } from '../components/ui.tsx';
import { AreaChart } from '../charts/AreaChart.tsx';
import { BarList } from '../charts/BarList.tsx';
import { ColumnChart } from '../charts/ColumnChart.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { cacheKey } from '../hooks/cache.ts';
import { useQuery } from '../hooks/useQuery.ts';

const LINE_SERIES: Array<{ key: ServiceLine; series: number }> = [
  { key: 'subscriptions', series: 1 },
  { key: 'field', series: 2 },
  { key: 'remote', series: 3 },
];

export function OpsScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api } = useApp();
  const [status, setStatus] = useState<CallStatus | 'all'>('all');
  const ops = useQuery<OpsSummary>(cacheKey('ops-summary'), () => api.opsSummary(), { scopes: ['ops'] });
  const calls = useQuery<OpsCallsResponse>(cacheKey('ops-calls', { status }), () => api.opsCalls({ status }), { scopes: ['ops'] });
  const subs = useQuery<OpsSubscriptionsResponse>(cacheKey('ops-subs'), () => api.opsSubscriptions(), { scopes: ['ops'] });
  const asOf = summary?.asOf ?? ops.data?.asOf ?? '';
  const data = ops.data;

  if (ops.loading && !data) {
    return (
      <div className="grid ops">
        <Card span={12}>
          <Skeleton className="ch" />
        </Card>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="grid ops">
        <Card span={12}>
          <EmptyState icon="server" text={he.ops.emptyAll} />
        </Card>
      </div>
    );
  }

  const allCalls = calls.data?.items ?? [];
  const openRemote = allCalls.filter((c) => c.status !== 'done' && c.type === 'remote').length;
  const openField = allCalls.filter((c) => c.status !== 'done' && c.type === 'field').length;
  const revenue12 = data.revenueByLine.reduce((sum, m) => sum + m.totalAgorot, 0);

  return (
    <div className="grid ops">
      <div className="col-12 inline" style={{ justifyContent: 'space-between' }}>
        <DashedTag>{he.ops.demoTag}</DashedTag>
        <span className="note">{he.ops.demoNote}</span>
      </div>

      {/* ── אריחים ─────────────────────────────────────────────────── */}
      <div className="col-12 tiles t4">
        {/* the screen's ONE hero figure (REDESIGN §8) */}
        <Tile
          hero
          label={he.ops.openCalls}
          value={
            <>
              <Num>{data.calls.open}</Num>
              <span className="unit">{he.home.inProgress(data.calls.inProgress)}</span>
            </>
          }
          note={
            <>
              <Icon name="monitor" className="xs" /> {openRemote} {CALL_TYPE.remote.label} · <Icon name="car" className="xs" /> {openField} {CALL_TYPE.field.label}
            </>
          }
        />
        <Tile label={he.ops.doneThisMonth} value={<Num>{data.calls.doneThisMonth}</Num>} note={summary ? `1–${summary.period.dayOfMonth}` : ''} />
        <Tile
          label={he.ops.avgClose}
          value={
            <>
              <Num>{data.calls.avgCloseDays ?? '—'}</Num>
              <span className="unit">{he.app.days}</span>
            </>
          }
          note={he.ops.avgCloseNote}
        />
        <Tile
          label={he.ops.sentinel}
          value={
            <>
              <Num>{data.calls.fromSentinelThisMonth}</Num>
              <span className="unit">{he.ops.thisMonth}</span>
            </>
          }
          note={
            <>
              <Icon name="zap" className="xs" /> {he.ops.sentinelNote}
            </>
          }
        />
      </div>

      {/* ── C9 ─────────────────────────────────────────────────────── */}
      <Card span={7} className="chart-c" labelledBy="h-c9">
        <CardHead titleId="h-c9" title={he.ops.lineQuestion} sub={he.ops.lineSub} />
        <ColumnChart
          labels={data.revenueByLine.map((m) => monthShort(m.month))}
          partialIndex={data.revenueByLine.findIndex((m) => m.partial)}
          height={240}
          maxBar={22}
          series={LINE_SERIES.map((l) => ({
            key: l.key,
            label: SERVICE_LINE[l.key],
            series: l.series,
            values: data.revenueByLine.map((m) => m.byLine[l.key]),
          }))}
          summary={`${he.ops.lineQuestion} ${data.revenueByLine.map((m) => `${monthShort(m.month)} ${fmtMoney(m.totalAgorot)}`).join(', ')}`}
          tip={(index) => {
            const row = data.revenueByLine[index]!;
            return {
              title: monthName(row.month),
              rows: [
                ...LINE_SERIES.map((l) => ({ label: SERVICE_LINE[l.key], value: fmtMoney(row.byLine[l.key]), color: `var(--series-${l.series})`, square: true })),
                { label: he.app.total, value: fmtMoney(row.totalAgorot) },
              ],
            };
          }}
          table={{
            caption: he.ops.lineSub,
            head: ['חודש', ...LINE_SERIES.map((l) => SERVICE_LINE[l.key]), he.app.total],
            rows: data.revenueByLine.map((m) => [monthName(m.month), ...LINE_SERIES.map((l) => fmtMoney(m.byLine[l.key])), fmtMoney(m.totalAgorot)]),
          }}
        />
      </Card>

      {/* ── MRR ────────────────────────────────────────────────────── */}
      <Card span={5} className="mrr" labelledBy="h-mrr">
        <CardHead titleId="h-mrr" title={he.ops.mrrQuestion} sub={he.ops.mrrSub} />
        <div className="tiles t3" style={{ gap: '8px' }}>
          <Tile tight label={he.ops.mrr} value={<Money agorot={data.subscriptions.mrrAgorot} />} />
          <Tile tight label={he.ops.active} value={<Num>{data.subscriptions.active}</Num>} />
          <Tile tight label={he.ops.churn} value={<Num>{fmtRatio(data.subscriptions.churn12m)}</Num>} note={data.subscriptions.churnLabel} />
        </div>
        <AreaChart
          labels={data.mrrTrend.map((p) => monthShort(p.month))}
          values={data.mrrTrend.map((p) => p.mrrAgorot)}
          height={150}
          summary={`${he.ops.mrrQuestion} ${monthShort(data.mrrTrend[0]?.month ?? '')} ${fmtMoney(data.mrrTrend[0]?.mrrAgorot ?? 0)} → ${monthShort(
            data.mrrTrend[data.mrrTrend.length - 1]?.month ?? '',
          )} ${fmtMoney(data.mrrTrend[data.mrrTrend.length - 1]?.mrrAgorot ?? 0)}`}
          tip={(index) => {
            const point = data.mrrTrend[index]!;
            return {
              title: monthName(point.month),
              rows: [
                { label: he.ops.mrr, value: fmtMoney(point.mrrAgorot), color: 'var(--series-1)' },
                { label: he.ops.subs, value: String(point.activeCount) },
              ],
            };
          }}
          table={{
            caption: he.ops.mrrSub,
            head: ['חודש', he.ops.mrr, he.ops.subs],
            rows: data.mrrTrend.map((p) => [monthName(p.month), fmtMoney(p.mrrAgorot), String(p.activeCount)]),
          }}
        />
      </Card>

      {/* ── מנויים ─────────────────────────────────────────────────── */}
      <Card span={7} className="subs" labelledBy="h-subs">
        <CardHead titleId="h-subs" title={he.ops.subs} sub={he.ops.subsSub} />
        <div className="tbl-wrap desk-only">
          <table className="tbl compact">
            <thead>
              <tr>
                <th scope="col">{he.ops.colCustomer}</th>
                <th scope="col">{he.ops.colPlan}</th>
                <th scope="col" className="n">
                  {he.ops.colSeats}
                </th>
                <th scope="col" className="n">
                  {he.ops.colMonthly}
                </th>
                <th scope="col">{he.ops.colBilling}</th>
                <th scope="col">{he.ops.colNextCharge}</th>
                <th scope="col">{he.ops.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {(subs.data?.items ?? []).map((sub) => (
                <tr key={sub.id}>
                  <td>{sub.customerName}</td>
                  <td>{sub.plan}</td>
                  <td className="n">
                    <Num>{sub.seats}</Num>
                  </td>
                  <td className="n">
                    <Money agorot={sub.monthlyNetAgorot} />
                  </td>
                  <td>{sub.billingMethod === 'standing_order' ? he.ops.billingStandingOrder : he.ops.billingInvoice}</td>
                  <td>{sub.nextChargeDate ? <Num>{fmtDate(sub.nextChargeDate)}</Num> : <span className="note">{he.app.none}</span>}</td>
                  <td>
                    <Chip tone={SUBSCRIPTION_STATUS[sub.status].chip as 'good' | 'warn' | 'outline'} icon={SUBSCRIPTION_STATUS[sub.status].icon} small>
                      {SUBSCRIPTION_STATUS[sub.status].label}
                      {sub.cancelledAt ? ` ${fmtShortDate(sub.cancelledAt, asOf)}` : ''}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>{he.ops.mrrRow(data.subscriptions.active)}</td>
                <td className="n">
                  <Money agorot={data.subscriptions.mrrAgorot} />
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="list mob-only" style={{ flexDirection: 'column' }}>
          {(subs.data?.items ?? []).map((sub) => (
            <div className="list-item" role="listitem" key={sub.id}>
              <div className="li-main">
                <div className="li-t">{sub.customerName}</div>
                <div className="li-s">
                  {sub.plan} · {he.ops.seatsShort(sub.seats)} · {sub.billingMethod === 'standing_order' ? he.ops.billingStandingOrder : he.invoices.termsNet30}
                </div>
              </div>
              <div className="li-end">
                <span className="li-amt">
                  <Money agorot={sub.monthlyNetAgorot} />
                </span>
                <Chip tone={SUBSCRIPTION_STATUS[sub.status].chip as 'good' | 'warn' | 'outline'} icon={SUBSCRIPTION_STATUS[sub.status].icon} small>
                  {SUBSCRIPTION_STATUS[sub.status].label}
                </Chip>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ── לקוחות מובילים ─────────────────────────────────────────── */}
      <Card span={5} className="top" labelledBy="h-top">
        <CardHead titleId="h-top" title={he.ops.top} sub={he.ops.topSub(fmtMoney(revenue12))} />
        <BarList
          ariaLabel={he.ops.topAria}
          items={data.topCustomers.map((customer) => ({
            key: customer.customerId,
            label: customer.customerName,
            value: customer.revenue12mAgorot,
            valueLabel: (
              <>
                <Money agorot={customer.revenue12mAgorot} /> <span className="sub">{fmtRatio(customer.share)}</span>
              </>
            ),
            title: `${customer.customerName}: ${fmtMoney(customer.revenue12mAgorot)}`,
          }))}
        />
      </Card>

      {/* ── קריאות ─────────────────────────────────────────────────── */}
      <Card span={12} className="calls" labelledBy="h-calls">
        <CardHead titleId="h-calls" title={he.ops.calls} sub={he.ops.callsSub(allCalls.length)} />
        <div className="chips" role="group" aria-label={he.ops.callsFilterAria}>
          <FilterChip label={he.ops.filterAll} pressed={status === 'all'} onClick={() => setStatus('all')} />
          <FilterChip label={he.ops.filterOpen} pressed={status === 'open'} onClick={() => setStatus('open')} />
          <FilterChip label={he.ops.filterInProgress} pressed={status === 'in_progress'} onClick={() => setStatus('in_progress')} />
          <FilterChip label={he.ops.filterDone} pressed={status === 'done'} onClick={() => setStatus('done')} />
        </div>
        {allCalls.length ? (
          <>
            <div className="tbl-wrap desk-only">
              <table className="tbl compact">
                <thead>
                  <tr>
                    <th scope="col">{he.ops.colNumber}</th>
                    <th scope="col">{he.ops.colCustomer}</th>
                    <th scope="col">{he.ops.colType}</th>
                    <th scope="col">{he.ops.colOpened}</th>
                    <th scope="col">{he.ops.colStatus}</th>
                    <th scope="col">{he.ops.colTech}</th>
                    <th scope="col" className="n">
                      {he.ops.colPrice}
                    </th>
                    <th scope="col">{he.ops.colSource}</th>
                  </tr>
                </thead>
                <tbody>
                  {allCalls.map((call) => (
                    <tr key={call.id}>
                      <td>
                        <Ltr className="mono">{call.id}</Ltr>
                      </td>
                      <td>{call.customerName}</td>
                      <td>
                        <Icon name={CALL_TYPE[call.type].icon} className="xs" /> {CALL_TYPE[call.type].label}
                      </td>
                      <td>
                        <Num>{fmtShortDate(call.openedAt.slice(0, 10), asOf)}</Num> <span className="note num">{fmtTime(call.openedAt)}</span>
                      </td>
                      <td>
                        <Chip tone={CALL_STATUS[call.status].chip as 'good' | 'warn' | 'info'} icon={CALL_STATUS[call.status].icon} small>
                          {CALL_STATUS[call.status].label}
                        </Chip>
                      </td>
                      <td>{call.technician ?? <span className="note">{he.app.none}</span>}</td>
                      <td className="n">
                        {call.priceNetAgorot == null ? (
                          <span className="note">{he.ops.inPlan}</span>
                        ) : (
                          <>
                            <Money agorot={call.priceNetAgorot} />
                            {call.estimate ? <span className="note"> {he.ops.estimate}</span> : null}
                          </>
                        )}
                      </td>
                      <td>
                        {call.source === 'sentinel' ? (
                          <Chip tone="accent" icon="zap" small>
                            {he.ops.sourceSentinel}
                            {call.finding ? ` · ${call.finding}` : ''}
                          </Chip>
                        ) : (
                          he.ops.sourceCustomer
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="list mob-only" style={{ flexDirection: 'column' }}>
              {allCalls.map((call) => (
                <div className="list-item" role="listitem" key={call.id}>
                  <div className="li-main">
                    <div className="li-t">
                      <Ltr className="mono">{call.id}</Ltr> · {call.customerName}
                    </div>
                    <div className="li-s">
                      {CALL_TYPE[call.type].label} · <Num>{fmtShortDate(call.openedAt.slice(0, 10), asOf)}</Num> · {call.technician ?? he.ops.noTech}
                      {call.source === 'sentinel' ? (
                        <Chip tone="accent" icon="zap" small>
                          {he.ops.sourceSentinel}
                        </Chip>
                      ) : null}
                    </div>
                  </div>
                  <div className="li-end">
                    <Chip tone={CALL_STATUS[call.status].chip as 'good' | 'warn' | 'info'} icon={CALL_STATUS[call.status].icon} small>
                      {CALL_STATUS[call.status].label}
                    </Chip>
                    <span className="note">{call.priceNetAgorot == null ? he.ops.inPlan : fmtMoney(call.priceNetAgorot)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text={he.ops.emptyCalls} />
        )}
      </Card>
    </div>
  );
}
